import React, {useMemo} from 'react';
import {RouteInfo} from 'sfccAPI/interfaces';

import {useLocalizeContent, useSSRResponse, useSuspenseCmsContent} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';
import resolvePagePlaceholders from 'coreUtils/resolvePagePlaceholders';
import {isServer} from 'coreUtils/utils';

interface MergedQuery {
	q: string,
	cgid: string,
}

interface Props {
	routeInfo: RouteInfo
	ComponentToLoad: React.Component,
	mergedQuery: MergedQuery
}

interface TemplateCategoryProps {
	routeInfo: RouteInfo
	ComponentToLoad: React.Component,
	mergedQuery: MergedQuery
	categoryId: string
	searchQuery: string
}

const CustomTemplateCategory = (
	{
		routeInfo,
		ComponentToLoad,
		categoryId,
		searchQuery
	}: TemplateCategoryProps
) => {
	const res = useSSRResponse();
	const config = useMemo(() => {
		if (searchQuery) {
			return {
				variables: {type: 'SEARCH'},
			};
		} else {
			return {
				variables: {category: categoryId},
				fallbackVariables: [{type: 'DEFAULT'}]
			};
		}
	}, [categoryId, searchQuery]);

	const {data} = useSuspenseCmsContent({
		contentType: 'categoryPage',
		config,
		collectionKey: 'categoryPageCollection'
	});

	const localizedContent = useLocalizeContent(data);
	const localizedItem = localizedContent?.categoryPageCollection?.items?.[0];
	useMemo(() => resolvePagePlaceholders(localizedItem), [localizedContent]);

	if (isServer && res?.cacheTagManager) {
		res.cacheTagManager.addContentfulTag();
		data?.categoryPageCollection?.items?.forEach((c) => res.cacheTagManager.addContentfulTag(c.sys.id));
	}

	return (
		<ComponentToLoad
			routeInfo={routeInfo}
			content={data?.categoryPageCollection?.items?.[0]}
			localizedContent={localizedItem}
		/>
	);
};

export default (props: Props) => {
	const {
		routeInfo,
		mergedQuery,
		ComponentToLoad
	} = props;
	const categoryId = mergedQuery.cgid as string | undefined;
	const category = useCategory(categoryId);
	const searchQuery = mergedQuery.q as string | undefined;

	if ((categoryId && !category?.category?.c_isDefaultPLPTemplate) || searchQuery) {
		return <CustomTemplateCategory {...props} searchQuery={searchQuery} categoryId={categoryId} />;
	}

	return (
		<ComponentToLoad
			routeInfo={routeInfo}
		/>
	);
};